import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolderPlus, faLock, faUnlock, faEye, faTrash, faEdit, faPlus,
  faSpinner, faFolder, faTimes, faShieldAlt, faBoxOpen, faArrowLeft,
  faExclamationTriangle, faCheckCircle, faSearch, faStar,
} from '@fortawesome/free-solid-svg-icons';
import { hiddenFoldersAPI, itemsAPI, categoriesAPI } from '../services/api';
import ItemDetailView from '../components/ItemDetailView';
import PasswordLockModal from '../components/PasswordLockModal';
import '../styles/HiddenFolders.css';
import useI18n from '../hooks/useI18n';

interface HiddenFolder {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  _count?: { items: number };
  createdAt: string;
}

interface FolderItem {
  id: string;
  addedAt: string;
  item: {
    id: string;
    name: string;
    description?: string;
    condition?: string;
    category?: { name: string; icon?: string };
    images?: string[];
    estimatedValue?: number;
    currency?: string;
  };
}

const FOLDER_COLORS = ['#6C5CE7', '#00B894', '#E17055', '#0984E3', '#FDCB6E', '#E84393', '#00CEC9', '#636E72'];
const FOLDER_ICONS = ['🔒', '💎', '📦', '🏷️', '🎁', '🔐', '🗄️', '💼', '🛡️', '⭐'];

const HiddenFolders: React.FC = () => {
  const { t } = useI18n();
  const tc = (name: string) => { const key = `categoryName.${name}` as any; const translated = t(key); return translated !== key ? translated : name; };
  const navigate = useNavigate();
  const [folders, setFolders] = useState<HiddenFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Create folder modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', password: '', confirmPassword: '', icon: '🔒', color: '#6C5CE7' });
  const [creating, setCreating] = useState(false);

  // Access folder
  const [accessFolderId, setAccessFolderId] = useState<string | null>(null);
  const [accessPassword, setAccessPassword] = useState('');
  const [accessError, setAccessError] = useState('');
  const [accessLoading, setAccessLoading] = useState(false);

  // Open folder view
  const [openFolder, setOpenFolder] = useState<HiddenFolder | null>(null);
  const [folderItems, setFolderItems] = useState<FolderItem[]>([]);

  // Add item modal
  const [showAddItem, setShowAddItem] = useState(false);
  const [allItems, setAllItems] = useState<any[]>([]);
  const [itemSearch, setItemSearch] = useState('');
  const [addingItem, setAddingItem] = useState<string | null>(null);
  const [addPassword, setAddPassword] = useState('');
  const [showAddPassword, setShowAddPassword] = useState<string | null>(null);
  const [addFilterCategory, setAddFilterCategory] = useState('all');
  const [addFilterFavorites, setAddFilterFavorites] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);

  // Edit folder
  const [editFolder, setEditFolder] = useState<HiddenFolder | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', icon: '', color: '', currentPassword: '', newPassword: '', confirmNewPassword: '' });

  // Delete folder
  const [deleteFolder, setDeleteFolder] = useState<HiddenFolder | null>(null);
  const [deletePassword, setDeletePassword] = useState('');

  // Item detail view
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockTargetItem, setLockTargetItem] = useState<any | null>(null);

  const handleViewFolderItem = (item: any) => {
    if (item.isLocked) {
      setLockTargetItem(item);
      setShowLockModal(true);
    } else {
      setSelectedItem(item);
      setShowDetailView(true);
    }
  };

  const handleLockSuccess = () => {
    setShowLockModal(false);
    if (lockTargetItem) {
      setSelectedItem(lockTargetItem);
      setShowDetailView(true);
      setLockTargetItem(null);
    }
  };

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const fetchFolders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await hiddenFoldersAPI.list();
      setFolders(res.data);
    } catch (err) {
      console.error('Failed to fetch folders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const handleCreate = async () => {
    if (!createForm.name || !createForm.password) {
      showMsg('error', t('hiddenFolders.nameAndPasswordRequired'));
      return;
    }
    if (createForm.password !== createForm.confirmPassword) {
      showMsg('error', t('hiddenFolders.passwordsNoMatch'));
      return;
    }
    if (createForm.password.length < 4) {
      showMsg('error', t('hiddenFolders.passwordMinLength'));
      return;
    }
    try {
      setCreating(true);
      await hiddenFoldersAPI.create({
        name: createForm.name,
        description: createForm.description,
        password: createForm.password,
        icon: createForm.icon,
        color: createForm.color,
      });
      setShowCreate(false);
      setCreateForm({ name: '', description: '', password: '', confirmPassword: '', icon: '🔒', color: '#6C5CE7' });
      showMsg('success', t('hiddenFolders.folderCreated'));
      fetchFolders();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || t('hiddenFolders.failedToCreateFolder'));
    } finally {
      setCreating(false);
    }
  };

  const handleAccess = async (folder: HiddenFolder) => {
    if (!accessPassword) {
      setAccessError(t('hiddenFolders.enterFolderPassword'));
      return;
    }
    try {
      setAccessLoading(true);
      setAccessError('');
      const res = await hiddenFoldersAPI.access(folder.id, accessPassword);
      setFolderItems(res.data.items || []);
      setOpenFolder(folder);
      setAccessFolderId(null);
      setAccessPassword('');
    } catch (err: any) {
      setAccessError(err.response?.data?.error || t('hiddenFolders.wrongPassword'));
    } finally {
      setAccessLoading(false);
    }
  };

  const handleAddItemToFolder = async (itemId: string) => {
    if (!openFolder) return;
    if (!addPassword) {
      setShowAddPassword(itemId);
      return;
    }
    try {
      setAddingItem(itemId);
      await hiddenFoldersAPI.addItem(openFolder.id, itemId, addPassword);
      // Refresh folder items
      const res = await hiddenFoldersAPI.access(openFolder.id, addPassword);
      setFolderItems(res.data.items || []);
      showMsg('success', t('hiddenFolders.itemAdded'));
      fetchFolders();
      // Refresh allItems to exclude the newly added item - keep password stored for consecutive adds
      const itemsRes = await itemsAPI.getAll();
      const allFetchedItems = itemsRes.data.items || itemsRes.data || [];
      const hiddenItemIds = new Set(res.data.items?.map((fi: any) => fi.item.id) || []);
      setAllItems(allFetchedItems.filter((item: any) => !hiddenItemIds.has(item.id)));
      setShowAddPassword(null);
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || t('hiddenFolders.failedToAddItem'));
    } finally {
      setAddingItem(null);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!openFolder) return;
    try {
      await hiddenFoldersAPI.removeItem(openFolder.id, itemId);
      setFolderItems(prev => prev.filter(fi => fi.item.id !== itemId));
      showMsg('success', t('hiddenFolders.itemRemoved'));
      fetchFolders();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || t('hiddenFolders.failedToRemoveItem'));
    }
  };

  const handleEditFolder = async () => {
    if (!editFolder) return;
    // Validate password change if user wants to change it
    if (editForm.newPassword) {
      if (!editForm.currentPassword) {
        showMsg('error', t('hiddenFolders.currentPasswordRequired'));
        return;
      }
      if (editForm.newPassword.length < 4) {
        showMsg('error', t('hiddenFolders.newPasswordMinLength'));
        return;
      }
      if (editForm.newPassword !== editForm.confirmNewPassword) {
        showMsg('error', t('hiddenFolders.newPasswordsNoMatch'));
        return;
      }
    }
    try {
      const updatePayload: any = {
        name: editForm.name,
        description: editForm.description,
        icon: editForm.icon,
        color: editForm.color,
      };
      if (editForm.newPassword && editForm.currentPassword) {
        updatePayload.currentPassword = editForm.currentPassword;
        updatePayload.newPassword = editForm.newPassword;
      }
      await hiddenFoldersAPI.update(editFolder.id, updatePayload);
      setEditFolder(null);
      showMsg('success', editForm.newPassword ? t('hiddenFolders.folderUpdatedAndPasswordChanged') : t('hiddenFolders.folderUpdated'));
      fetchFolders();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || t('hiddenFolders.failedToUpdateFolder'));
    }
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolder || !deletePassword) return;
    try {
      await hiddenFoldersAPI.delete(deleteFolder.id, deletePassword);
      setDeleteFolder(null);
      setDeletePassword('');
      if (openFolder?.id === deleteFolder.id) {
        setOpenFolder(null);
        setFolderItems([]);
      }
      showMsg('success', t('hiddenFolders.folderDeleted'));
      fetchFolders();
    } catch (err: any) {
      showMsg('error', err.response?.data?.error || t('hiddenFolders.failedToDeleteFolder'));
    }
  };

  const openAddItemModal = async () => {
    setShowAddItem(true);
    setItemSearch('');
    setAddFilterCategory('all');
    setAddFilterFavorites(false);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        itemsAPI.getAll(),
        categoriesAPI.getAll(),
      ]);
      const allFetchedItems = itemsRes.data.items || itemsRes.data || [];
      // Filter out items already in this hidden folder
      const hiddenIds = new Set(folderItems.map(fi => fi.item.id));
      setAllItems(allFetchedItems.filter((item: any) => !hiddenIds.has(item.id)));
      setCategories(catsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch items:', err);
    }
  };

  const filteredItems = allItems.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(itemSearch.toLowerCase());
    const matchesCategory = addFilterCategory === 'all' || item.category?.id === addFilterCategory;
    const matchesFavorites = !addFilterFavorites || item.isFavorite;
    return matchesSearch && matchesCategory && matchesFavorites;
  });

  const existingItemIds = new Set(folderItems.map(fi => fi.item.id));

  // Open folder detail view
  if (openFolder) {
    return (
      <div className="hidden-folders">
        {message && (
          <div className={`hf-message ${message.type}`}>
            <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle} />
            {message.text}
          </div>
        )}

        <div className="hf-header">
          <button className="btn-back" onClick={() => { setOpenFolder(null); setFolderItems([]); }}>
            <FontAwesomeIcon icon={faArrowLeft} /> {t('hiddenFolders.back')}
          </button>
          <div className="hf-folder-title">
            <span className="folder-icon-large" style={{ color: openFolder.color }}>{openFolder.icon || '🔒'}</span>
            <div>
              <h2>{openFolder.name}</h2>
              {openFolder.description && <p>{openFolder.description}</p>}
            </div>
          </div>
          <div className="hf-folder-actions">
            <button className="btn-icon" onClick={() => { setEditFolder(openFolder); setEditForm({ name: openFolder.name, description: openFolder.description || '', icon: openFolder.icon || '🔒', color: openFolder.color || '#6C5CE7', currentPassword: '', newPassword: '', confirmNewPassword: '' }); }}>
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button className="btn-icon danger" onClick={() => setDeleteFolder(openFolder)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>

        <div className="hf-items-header">
          <h3><FontAwesomeIcon icon={faBoxOpen} /> Items ({folderItems.length})</h3>
          <button className="btn-primary btn-compact" onClick={openAddItemModal}>
            <FontAwesomeIcon icon={faPlus} /> {t('hiddenFolders.addItem')}
          </button>
        </div>

        {folderItems.length === 0 ? (
          <div className="hf-empty">
            <FontAwesomeIcon icon={faBoxOpen} />
            <p>{t('hiddenFolders.noItems')}</p>
            <button className="btn-primary" onClick={openAddItemModal}>
              <FontAwesomeIcon icon={faPlus} /> {t('hiddenFolders.addFirstItem')}
            </button>
          </div>
        ) : (
          <div className="hf-items-grid">
            {folderItems.map(fi => (
              <div key={fi.item.id} className="hf-item-card" onClick={() => handleViewFolderItem(fi.item)} style={{ cursor: 'pointer' }}>
                <div className="hf-item-info">
                  <h4>{fi.item.name}</h4>
                  {fi.item.category && <span className="hf-item-category">{fi.item.category.icon} {tc(fi.item.category.name)}</span>}
                  {fi.item.condition && (
                    <span className="hf-item-category">{t(`condition.${fi.item.condition.toLowerCase().replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase())}`) || fi.item.condition}</span>
                  )}
                  {fi.item.estimatedValue && (
                    <span className="hf-item-value">
                      {fi.item.currency || '$'}{fi.item.estimatedValue.toLocaleString()}
                    </span>
                  )}
                </div>
                <button className="btn-icon danger" onClick={(e) => { e.stopPropagation(); handleRemoveItem(fi.item.id); }} title={t('hiddenFolders.removeFromFolder')}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add Item Modal */}
        {showAddItem && (
          <div className="modal-overlay" onClick={() => { setShowAddItem(false); setShowAddPassword(null); setAddPassword(''); }}>
            <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{t('hiddenFolders.addItemTo')} {openFolder.name}</h3>
                <button className="btn-close" onClick={() => { setShowAddItem(false); setShowAddPassword(null); setAddPassword(''); }}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="modal-body">
                <div className="search-box">
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    type="text"
                    placeholder={t('hiddenFolders.searchItems')}
                    value={itemSearch}
                    onChange={e => setItemSearch(e.target.value)}
                  />
                </div>

                <div className="hf-add-filters">
                  <select
                    className="hf-filter-select"
                    value={addFilterCategory}
                    onChange={e => setAddFilterCategory(e.target.value)}
                  >
                    <option value="all">{t('hiddenFolders.allCategories')}</option>
                    {categories.map((cat: any) => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {tc(cat.name)}</option>
                    ))}
                  </select>
                  <button
                    className={`hf-filter-btn ${addFilterFavorites ? 'active' : ''}`}
                    onClick={() => setAddFilterFavorites(prev => !prev)}
                    title={t('hiddenFolders.favoritesOnly')}
                  >
                    <FontAwesomeIcon icon={faStar} /> {t('hiddenFolders.favorites')}
                  </button>
                </div>

                {showAddPassword && (
                  <div className="add-password-prompt">
                    <p>{t('hiddenFolders.enterPasswordToAdd')}</p>
                    <input
                      type="password"
                      value={addPassword}
                      onChange={e => setAddPassword(e.target.value)}
                      placeholder={t('hiddenFolders.folderPasswordPlaceholder')}
                      onKeyDown={e => e.key === 'Enter' && handleAddItemToFolder(showAddPassword)}
                    />
                    <button className="btn-primary" onClick={() => handleAddItemToFolder(showAddPassword)} disabled={!addPassword}>
                      {t('hiddenFolders.confirm')}
                    </button>
                  </div>
                )}

                <div className="items-list">
                  {filteredItems.map(item => (
                    <div key={item.id} className={`item-row ${existingItemIds.has(item.id) ? 'already-added' : ''}`}>
                      <div className="item-row-info">
                        <span className="item-row-name">
                          {item.isFavorite && <FontAwesomeIcon icon={faStar} className="item-row-fav" />}
                          {item.name}
                        </span>
                        {item.category?.name && <span className="item-row-cat">{item.category.icon || ''} {tc(item.category.name)}</span>}
                      </div>
                      {existingItemIds.has(item.id) ? (
                        <span className="already-badge">{t('hiddenFolders.alreadyAdded')}</span>
                      ) : (
                        <button
                          className="btn-add-item"
                          onClick={() => {
                            if (addPassword) {
                              handleAddItemToFolder(item.id);
                            } else {
                              setShowAddPassword(item.id);
                            }
                          }}
                          disabled={addingItem === item.id}
                        >
                          {addingItem === item.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
                        </button>
                      )}
                    </div>
                  ))}
                  {filteredItems.length === 0 && <p className="no-results">{t('hiddenFolders.noResults')}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Folder Modal */}
        {editFolder && (
          <div className="modal-overlay" onClick={() => setEditFolder(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{t('hiddenFolders.editFolder')}</h3>
                <button className="btn-close" onClick={() => setEditFolder(null)}><FontAwesomeIcon icon={faTimes} /></button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>{t('hiddenFolders.nameLabel')}</label>
                  <input type="text" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t('hiddenFolders.descriptionLabel')}</label>
                  <textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t('hiddenFolders.iconLabel')}</label>
                  <div className="icon-picker">
                    {FOLDER_ICONS.map(ic => (
                      <button key={ic} className={`icon-btn ${editForm.icon === ic ? 'selected' : ''}`} onClick={() => setEditForm(p => ({ ...p, icon: ic }))}>{ic}</button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('hiddenFolders.colorLabel')}</label>
                  <div className="color-picker">
                    {FOLDER_COLORS.map(c => (
                      <button key={c} className={`color-btn ${editForm.color === c ? 'selected' : ''}`} style={{ background: c }} onClick={() => setEditForm(p => ({ ...p, color: c }))} />
                    ))}
                  </div>
                </div>

                {/* Password Change Section */}
                <div className="form-divider" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '16px 0', paddingTop: '16px' }}>
                  <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    <FontAwesomeIcon icon={faLock} style={{ marginRight: '6px' }} />
                    {t('hiddenFolders.changeFolderPassword')}
                  </label>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '12px' }}>{t('hiddenFolders.leaveEmptyKeep')}</p>
                  <div className="form-group">
                    <label>{t('hiddenFolders.currentPassword')}</label>
                    <input
                      type="password"
                      value={editForm.currentPassword}
                      onChange={e => setEditForm(p => ({ ...p, currentPassword: e.target.value }))}
                      placeholder={t('hiddenFolders.currentPasswordPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('hiddenFolders.newPassword')}</label>
                    <input
                      type="password"
                      value={editForm.newPassword}
                      onChange={e => setEditForm(p => ({ ...p, newPassword: e.target.value }))}
                      placeholder={t('hiddenFolders.newPasswordPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('hiddenFolders.confirmNewPassword')}</label>
                    <input
                      type="password"
                      value={editForm.confirmNewPassword}
                      onChange={e => setEditForm(p => ({ ...p, confirmNewPassword: e.target.value }))}
                      placeholder={t('hiddenFolders.confirmPasswordPlaceholder')}
                    />
                  </div>
                </div>

                <button className="btn-primary full-width" onClick={handleEditFolder}>{t('hiddenFolders.saveChanges')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Folder Modal */}
        {deleteFolder && (
          <div className="modal-overlay" onClick={() => { setDeleteFolder(null); setDeletePassword(''); }}>
            <div className="modal-content modal-danger" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3><FontAwesomeIcon icon={faExclamationTriangle} /> {t('hiddenFolders.deleteFolder')}</h3>
                <button className="btn-close" onClick={() => { setDeleteFolder(null); setDeletePassword(''); }}><FontAwesomeIcon icon={faTimes} /></button>
              </div>
              <div className="modal-body">
                <p>{t('hiddenFolders.deleteConfirm')} <strong>{deleteFolder.name}</strong>{t('hiddenFolders.deleteDesc')}</p>
                <div className="form-group">
                  <label>{t('hiddenFolders.deletePasswordPrompt')}</label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                    placeholder={t('hiddenFolders.folderPasswordPlaceholder')}
                    onKeyDown={e => e.key === 'Enter' && handleDeleteFolder()}
                  />
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={() => { setDeleteFolder(null); setDeletePassword(''); }}>{t('hiddenFolders.cancel')}</button>
                  <button className="btn-danger" onClick={handleDeleteFolder} disabled={!deletePassword}>{t('hiddenFolders.deleteFolder')}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Item Detail View */}
        {selectedItem && (
          <ItemDetailView
            item={selectedItem}
            isOpen={showDetailView}
            onClose={() => { setShowDetailView(false); setSelectedItem(null); }}
            onEdit={(item: any) => { setShowDetailView(false); setSelectedItem(null); navigate('/library', { state: { editItem: item } }); }}
            onDelete={() => { setShowDetailView(false); setSelectedItem(null); }}
            onToggleFavorite={() => {}}
          />
        )}

        {/* Lock Gate Modal */}
        {showLockModal && lockTargetItem && (
          <PasswordLockModal
            itemId={lockTargetItem.id}
            itemName={lockTargetItem.name}
            isLocked={true}
            mode="verify"
            onClose={() => { setShowLockModal(false); setLockTargetItem(null); }}
            onSuccess={handleLockSuccess}
          />
        )}
      </div>
    );
  }

  // Folder list view
  return (
    <div className="hidden-folders">
      {message && (
        <div className={`hf-message ${message.type}`}>
          <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle} />
          {message.text}
        </div>
      )}

      <div className="hf-header">
        <div>
          <h2><FontAwesomeIcon icon={faShieldAlt} /> {t('hiddenFolders.title')}</h2>
          <p>{t('hiddenFolders.subtitle')}</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          <FontAwesomeIcon icon={faFolderPlus} /> {t('hiddenFolders.newFolder')}
        </button>
      </div>

      {loading ? (
        <div className="hf-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>{t('hiddenFolders.loading')}</p>
        </div>
      ) : folders.length === 0 ? (
        <div className="hf-empty">
          <FontAwesomeIcon icon={faFolder} />
          <h3>{t('hiddenFolders.noFolders')}</h3>
          <p>{t('hiddenFolders.noFoldersDesc')}</p>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <FontAwesomeIcon icon={faFolderPlus} /> {t('hiddenFolders.createFirst')}
          </button>
        </div>
      ) : (
        <div className="hf-grid">
          {folders.map(folder => (
            <div key={folder.id} className="hf-card" style={{ borderColor: folder.color || '#6C5CE7' }}>
              <div className="hf-card-icon" style={{ background: folder.color || '#6C5CE7' }}>
                {folder.icon || '🔒'}
              </div>
              <div className="hf-card-body">
                <h3>{folder.name}</h3>
                {folder.description && <p>{folder.description}</p>}
                <span className="hf-card-count">
                  <FontAwesomeIcon icon={faBoxOpen} /> {folder._count?.items || 0} {t('hiddenFolders.items')}
                </span>
              </div>
              <div className="hf-card-actions">
                <button className="btn-unlock" onClick={() => { setAccessFolderId(folder.id); setAccessPassword(''); setAccessError(''); }}>
                  <FontAwesomeIcon icon={faUnlock} /> {t('hiddenFolders.open')}
                </button>
                <button className="btn-icon danger" onClick={() => setDeleteFolder(folder)} title={t('hiddenFolders.deleteFolder')}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Password Access Modal */}
      {accessFolderId && (
        <div className="modal-overlay" onClick={() => { setAccessFolderId(null); setAccessPassword(''); setAccessError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FontAwesomeIcon icon={faLock} /> {t('hiddenFolders.enterPassword')}</h3>
              <button className="btn-close" onClick={() => { setAccessFolderId(null); setAccessPassword(''); setAccessError(''); }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p>{t('hiddenFolders.passwordProtected')}</p>
              <div className="form-group">
                <input
                  type="password"
                  value={accessPassword}
                  onChange={e => { setAccessPassword(e.target.value); setAccessError(''); }}
                  placeholder={t('hiddenFolders.folderPassword')}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const folder = folders.find(f => f.id === accessFolderId);
                      if (folder) handleAccess(folder);
                    }
                  }}
                />
                {accessError && <span className="field-error">{accessError}</span>}
              </div>
              <button
                className="btn-primary full-width"
                onClick={() => {
                  const folder = folders.find(f => f.id === accessFolderId);
                  if (folder) handleAccess(folder);
                }}
                disabled={accessLoading}
              >
                {accessLoading ? <><FontAwesomeIcon icon={faSpinner} spin /> {t('hiddenFolders.verifying')}</> : <><FontAwesomeIcon icon={faEye} /> {t('hiddenFolders.viewFolder')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Modal */}
      {deleteFolder && (
        <div className="modal-overlay" onClick={() => { setDeleteFolder(null); setDeletePassword(''); }}>
          <div className="modal-content modal-danger" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FontAwesomeIcon icon={faExclamationTriangle} /> {t('hiddenFolders.deleteFolder')}</h3>
              <button className="btn-close" onClick={() => { setDeleteFolder(null); setDeletePassword(''); }}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="modal-body">
              <p>{t('hiddenFolders.deleteConfirm')} <strong>{deleteFolder.name}</strong>{t('hiddenFolders.deleteDesc')}</p>
              <div className="form-group">
                <label>{t('hiddenFolders.deletePasswordPrompt')}</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder={t('hiddenFolders.folderPasswordPlaceholder')}
                  onKeyDown={e => e.key === 'Enter' && handleDeleteFolder()}
                />
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => { setDeleteFolder(null); setDeletePassword(''); }}>{t('hiddenFolders.cancel')}</button>
                <button className="btn-danger" onClick={handleDeleteFolder} disabled={!deletePassword}>{t('hiddenFolders.deleteFolder')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FontAwesomeIcon icon={faFolderPlus} /> {t('hiddenFolders.createFolder')}</h3>
              <button className="btn-close" onClick={() => setShowCreate(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('hiddenFolders.folderName')}</label>
                <input type="text" value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder={t('hiddenFolders.folderNamePlaceholder')} />
              </div>
              <div className="form-group">
                <label>{t('hiddenFolders.descriptionLabel')}</label>
                <textarea value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))} placeholder={t('hiddenFolders.descriptionPlaceholder')} />
              </div>
              <div className="form-group">
                <label>{t('hiddenFolders.iconLabel')}</label>
                <div className="icon-picker">
                  {FOLDER_ICONS.map(ic => (
                    <button key={ic} className={`icon-btn ${createForm.icon === ic ? 'selected' : ''}`} onClick={() => setCreateForm(p => ({ ...p, icon: ic }))}>{ic}</button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>{t('hiddenFolders.colorLabel')}</label>
                <div className="color-picker">
                  {FOLDER_COLORS.map(c => (
                    <button key={c} className={`color-btn ${createForm.color === c ? 'selected' : ''}`} style={{ background: c }} onClick={() => setCreateForm(p => ({ ...p, color: c }))} />
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><FontAwesomeIcon icon={faLock} /> {t('hiddenFolders.password')}</label>
                  <input type="password" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} placeholder={t('hiddenFolders.folderPasswordPlaceholder')} />
                </div>
                <div className="form-group">
                  <label>{t('hiddenFolders.confirmPassword')}</label>
                  <input type="password" value={createForm.confirmPassword} onChange={e => setCreateForm(p => ({ ...p, confirmPassword: e.target.value }))} placeholder={t('hiddenFolders.confirmFolderPasswordPlaceholder')} />
                </div>
              </div>
              <button className="btn-primary full-width" onClick={handleCreate} disabled={creating}>
                {creating ? <><FontAwesomeIcon icon={faSpinner} spin /> {t('hiddenFolders.creating')}</> : <><FontAwesomeIcon icon={faFolderPlus} /> {t('hiddenFolders.createFolder')}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HiddenFolders;
